/**
 * Sidebar inbox con multi-account dropdown e tutte le cartelle.
 */
import {
  Inbox, Send, FileText, Trash2, AlertOctagon, Sparkles, Star,
  Receipt, HelpCircle, Bell, Mail, LogOut, Plus, Pencil, Settings, Archive,
  ChevronsUpDown, Check, Sun, Moon, Monitor, Search, Clock, Loader2, Users, Filter, CalendarDays,
  ConciergeBell, Rocket, Video, Archive as ArchiveIcon, CreditCard, Globe,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Popover from '@radix-ui/react-popover';
import { useAuth } from '@/stores/auth.store';
import { useInbox } from '@/stores/inbox.store';
import { useComposer } from '@/stores/composer.store';
import { useTheme } from '@/stores/theme.store';
import { usePalette } from '@/stores/palette.store';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { accountsApi, aiRepliesApi, billingApi, type EmailAccount, type FolderRole } from '@/lib/api/endpoints';
import { LabelsSection } from '@/components/layout/LabelsSection';
import { cn } from '@/lib/utils';
import { BlagomailMark } from '@/components/BlagomailMark';
import { toast } from 'sonner';

interface SidebarItemProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  count?: number;
  onClick: () => void;
}

function SidebarItem({ active, icon, label, count, onClick }: SidebarItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors text-left',
        active
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-foreground hover:bg-muted'
      )}
    >
      <span className="flex-shrink-0 [&_svg]:h-4 [&_svg]:w-4">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span className="text-xs text-muted-foreground">{count}</span>
      )}
    </button>
  );
}

export function InboxSidebar() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const selectedFolder = useInbox((s) => s.selectedFolderRole);
  const selectedCategory = useInbox((s) => s.selectedCategory);
  const setFolder = useInbox((s) => s.setFolder);
  const setCategory = useInbox((s) => s.setCategory);
  const selectedAccountId = useInbox((s) => s.selectedAccountId);
  const setAccount = useInbox((s) => s.setAccount);
  const openCompose = useComposer((s) => s.openCompose);
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);
  const openPalette = usePalette((s) => s.openIt);

  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
    refetchInterval: 30_000,
  });

  // Stessa chiave della pagina Abbonamento: la risposta è già in cache e non
  // costa una richiesta in più.
  const billingQuery = useQuery({
    queryKey: ['billing-me'],
    queryFn: () => billingApi.me(),
    staleTime: 5 * 60_000,
  });
  const usoCaselle = billingQuery.data?.usage.mailboxes.used ?? 0;
  const limiteCaselle = billingQuery.data?.usage.mailboxes.limit ?? 0;
  const pieno = !!billingQuery.data && usoCaselle >= limiteCaselle;

  const aiRepliesQuery = useQuery({
    queryKey: ['ai-replies'],
    queryFn: () => aiRepliesApi.list(),
    refetchInterval: 60_000,
  });
  const aiReplyCount = aiRepliesQuery.data?.suggestions.length ?? 0;

  const accounts = accountsQuery.data?.accounts ?? [];
  const currentAccount: EmailAccount | undefined =
    accounts.find((a) => a.id === selectedAccountId) ??
    accounts.find((a) => a.is_default) ??
    accounts[0];

  function selectFolder(role: FolderRole) {
    setFolder(role);
    navigate('/');
  }

  function handleLogout() {
    void logout();
    navigate('/login');
  }

  return (
    <aside className="w-64 bg-muted/30 border-r flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BlagomailMark size={26} />
          <span className="font-bold text-lg">Blagomail</span>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={openPalette} title="Cmd+K">
          <Search className="h-4 w-4" />
        </Button>
      </div>

      {accounts.length > 0 && (
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="mx-3 mt-3 flex items-center gap-2 p-2 rounded-md border hover:bg-accent text-left"
            >
              <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
                {currentAccount?.email_address.charAt(0).toUpperCase() ?? '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">
                  {currentAccount?.display_name || currentAccount?.email_address || 'Nessun account'}
                </div>
                <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1">
                  {currentAccount?.sync_status === 'syncing' && (
                    <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  )}
                  {currentAccount?.sync_status === 'error' && '⚠ '}
                  {currentAccount?.email_address}
                </div>
              </div>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content sideOffset={4} className="z-50 w-[224px] rounded-md border bg-popover shadow-md p-1">
              {accounts.map((acc) => (
                <button
                  key={acc.id}
                  type="button"
                  onClick={() => setAccount(acc.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-accent text-left"
                >
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
                    {acc.email_address.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="block truncate text-xs">{acc.email_address}</span>
                    {acc.shared && (
                      <span className="block text-[10px] text-muted-foreground truncate">
                        condivisa{acc.shared_by ? ` da ${acc.shared_by}` : ''}{acc.can_send === false ? ' · sola lettura' : ''}
                      </span>
                    )}
                  </div>
                  {acc.shared && <Users className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
                  {acc.id === currentAccount?.id && <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                </button>
              ))}
              <Separator className="my-1" />
              {/* Chiedere di compilare indirizzo, password e server per poi
                  rifiutare alla fine è una perdita di tempo che sembra un
                  guasto: se il piano è pieno lo si dice subito, e si porta
                  dove il problema si risolve. */}
              <button
                type="button"
                onClick={() => {
                  if (pieno) {
                    toast.info(`Il tuo piano include ${limiteCaselle} ${limiteCaselle === 1 ? 'casella' : 'caselle'}. Per aggiungerne altre serve un piano più capiente.`);
                    navigate('/abbonamento');
                    return;
                  }
                  navigate('/setup');
                }}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-accent text-left"
              >
                <span className="flex items-center gap-2">
                  <Plus className="h-3.5 w-3.5" />
                  <span className="text-xs">Aggiungi account</span>
                </span>
                {pieno && (
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {usoCaselle}/{limiteCaselle}
                  </span>
                )}
              </button>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}

      <div className="px-3 mt-3">
        <Button className="w-full justify-start gap-2" onClick={() => openCompose()}>
          <Pencil className="h-4 w-4" />
          Scrivi
        </Button>
      </div>

      <div className="px-3 mt-2">
        <button
          type="button"
          onClick={() => navigate('/ai-replies')}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary transition-colors"
        >
          <Sparkles className="h-4 w-4" />
          <span className="flex-1 text-left font-medium">Risposte AI</span>
          {aiReplyCount > 0 && (
            <span className="text-xs font-bold bg-primary text-primary-foreground rounded-full px-2 py-0.5 min-w-[20px] text-center">
              {aiReplyCount}
            </span>
          )}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 mt-4 space-y-5">
        <div className="space-y-0.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-3 py-1">
            Cartelle
          </div>
          <SidebarItem active={selectedFolder === 'inbox' && !selectedCategory}
            icon={<Inbox />} label="Inbox"
            onClick={() => { setCategory(null); selectFolder('inbox'); }} />
          <SidebarItem active={selectedFolder === 'drafts'}
            icon={<FileText />} label="Bozze"
            onClick={() => { setFolder('drafts'); navigate('/drafts'); }} />
          <SidebarItem active={selectedFolder === 'sent'}
            icon={<Send />} label="Inviati"
            onClick={() => selectFolder('sent')} />
          <SidebarItem active={selectedFolder === 'archive'}
            icon={<Archive />} label="Archivio"
            onClick={() => selectFolder('archive')} />
          <SidebarItem active={selectedFolder === 'spam'}
            icon={<AlertOctagon />} label="Spam"
            onClick={() => selectFolder('spam')} />
          <SidebarItem active={selectedFolder === 'trash'}
            icon={<Trash2 />} label="Cestino"
            onClick={() => selectFolder('trash')} />
        </div>

        <div className="space-y-0.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-3 py-1 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Categorie AI
          </div>
          <SidebarItem active={selectedCategory === 'preventivo'}
            icon={<FileText className="text-blue-600" />} label="Preventivi"
            onClick={() => { setCategory('preventivo'); navigate('/'); }} />
          <SidebarItem active={selectedCategory === 'fattura'}
            icon={<Receipt className="text-emerald-600" />} label="Fatture"
            onClick={() => { setCategory('fattura'); navigate('/'); }} />
          <SidebarItem active={selectedCategory === 'supporto'}
            icon={<HelpCircle className="text-amber-600" />} label="Supporto"
            onClick={() => { setCategory('supporto'); navigate('/'); }} />
          <SidebarItem active={selectedCategory === 'fyi'}
            icon={<Bell className="text-purple-600" />} label="FYI"
            onClick={() => { setCategory('fyi'); navigate('/'); }} />
        </div>

        <LabelsSection />

        <div className="space-y-0.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-3 py-1">
            Strumenti
          </div>
          <SidebarItem active={false} icon={<ConciergeBell className="text-indigo-500" />} label="La Reception"
            onClick={() => navigate('/reception')} />
          <SidebarItem active={false} icon={<Rocket className="text-indigo-500" />} label="Invio Blagomail"
            onClick={() => navigate('/delivery')} />
          <SidebarItem active={false} icon={<Sparkles />} label="Inbox Zero"
            onClick={() => navigate('/inbox-zero')} />
          <SidebarItem active={false} icon={<Star />} label="Speciali"
            onClick={() => navigate('/?starred=1')} />
          <SidebarItem active={false} icon={<CalendarDays />} label="Calendario"
            onClick={() => navigate('/calendar')} />
          <SidebarItem active={false} icon={<Video className="text-indigo-500" />} label="Riunioni"
            onClick={() => navigate('/meetings')} />
          <SidebarItem active={false} icon={<Clock />} label="Promemoria"
            onClick={() => navigate('/reminders')} />
          <SidebarItem active={false} icon={<Users />} label="Rubrica"
            onClick={() => navigate('/contacts')} />
          <SidebarItem active={false} icon={<Receipt />} label="Documenti"
            onClick={() => navigate('/documents')} />
          <SidebarItem active={false} icon={<ArchiveIcon className="text-indigo-500" />} label="Archivio"
            onClick={() => navigate('/archive')} />
          <SidebarItem active={false} icon={<Filter />} label="Regole"
            onClick={() => navigate('/rules')} />
          <SidebarItem active={false} icon={<Globe />} label="I tuoi domini"
            onClick={() => navigate('/domini')} />
          <SidebarItem active={false} icon={<CreditCard />} label="Abbonamento"
            onClick={() => navigate('/abbonamento')} />
          <SidebarItem active={false} icon={<Settings />} label="Impostazioni"
            onClick={() => navigate('/settings')} />
        </div>
      </nav>

      <div className="p-3 border-t flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {user?.full_name || user?.email}
          </div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark')}
          title={`Tema: ${theme}`}
        >
          {theme === 'dark' ? <Moon className="h-4 w-4" /> : theme === 'light' ? <Sun className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
        </Button>

        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleLogout} title="Esci">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </aside>
  );
}
